import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

/**
 * App-wide confirmation dialog. `requireTyped` enforces typing DELETE (destructive actions);
 * without it, it is a plain styled Confirm/Cancel (replaces window.confirm).
 * Usage: <ConfirmActionDialog open={!!pending} title="Delete Schedule?" message={<>…<b>{name}</b>…</>}
 *          requireTyped onConfirm={async () => {...}} onOpenChange={(o) => !o && setPending(null)} />
 */
export const ConfirmActionDialog = ({
  open, onOpenChange, title, message, confirmLabel = 'Delete', requireTyped = false,
  confirmWord = 'DELETE', destructive = true, onConfirm, testPrefix = 'confirm-action',
}) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setTyped(''); setBusy(false); } }, [open]);

  const run = async () => {
    setBusy(true);
    try { await onConfirm?.(); onOpenChange?.(false); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      <DialogContent className="max-w-sm" data-testid={`${testPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {destructive && <AlertTriangle className="w-5 h-5 text-red-500" />}{title}
          </DialogTitle>
          {message && <DialogDescription className="break-words">{message}</DialogDescription>}
        </DialogHeader>
        {requireTyped && (
          <div className="space-y-3">
            <p className="text-sm">To confirm, type the following exactly:</p>
            <code className="block p-2 bg-muted rounded text-sm font-mono font-bold">{confirmWord}</code>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type confirmation text..." className="font-mono" autoFocus data-testid={`${testPrefix}-input`} />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onOpenChange?.(false)} data-testid={`${testPrefix}-cancel`}>Cancel</Button>
          <Button size="sm" variant={destructive ? 'destructive' : 'default'} disabled={busy || (requireTyped && typed !== confirmWord)} onClick={run} data-testid={`${testPrefix}-confirm`}>
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
